// src/pages/PagePersonnalisee/PagePersonnalisee.jsx
// ou src/pages/Home/PagePersonnalisee.jsx selon ton arborescence

import React, { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaArrowLeft,
  FaExpandAlt,
  FaFileAlt,
  FaImage,
  FaLayerGroup,
  FaPhotoVideo,
  FaPlayCircle,
  FaTimes,
} from "react-icons/fa";

import {
  getPageBySlug,
  getAllArticlesByPage,
} from "./../../services/pagesPersonnaliseesServices";
import useScrollToTop from "./../../hooks/useScollToTop";

const API_BASE = process.env.REACT_APP_API_URL || "";
const FALLBACK_IMAGE = "/img/article_placeholder.jpg";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

/* =========================
   Helpers HTML / URL
========================= */

const sanitizeHtml = (html = "") => {
  return DOMPurify.sanitize(html || "", {
    USE_PROFILES: {
      html: true,
    },
    FORBID_ATTR: ["style"],
  });
};

const isExternalUrl = (url = "") => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

const resolveMediaUrl = (url = "") => {
  const cleanUrl = String(url || "").trim();

  if (!cleanUrl) return "";

  if (
    isExternalUrl(cleanUrl) ||
    cleanUrl.startsWith("data:") ||
    cleanUrl.startsWith("blob:")
  ) {
    return cleanUrl;
  }

  if (cleanUrl.startsWith("/img/")) return cleanUrl;

  if (cleanUrl.startsWith("/uploads/")) {
    return `${API_BASE.replace(/\/$/, "")}${cleanUrl}`;
  }

  if (cleanUrl.startsWith("uploads/")) {
    return `${API_BASE.replace(/\/$/, "")}/${cleanUrl}`;
  }

  if (cleanUrl.startsWith("/")) {
    return `${API_BASE.replace(/\/$/, "")}${cleanUrl}`;
  }

  return `${API_BASE.replace(/\/$/, "")}/${cleanUrl}`;
};

/* =========================
   Helpers médias
========================= */

const isImageUrl = (url = "") => {
  const clean = String(url || "").split("?")[0].toLowerCase();

  return (
    /\.(avif|gif|jpe?g|png|webp|svg)$/i.test(clean) ||
    url.includes("res.cloudinary.com/image")
  );
};

const isDirectVideoUrl = (url = "") => {
  const clean = String(url || "").split("?")[0].toLowerCase();

  return /\.(mp4|webm|ogg|ogv|mov|m4v)$/i.test(clean);
};

const getYouTubeEmbedSrc = (url = "") => {
  if (!url) return null;

  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./, "");

    if (host === "youtu.be") {
      const id = parsed.pathname.slice(1);
      return id ? `https://www.youtube.com/embed/${id}?rel=0` : null;
    }

    if (host.endsWith("youtube.com")) {
      if (parsed.pathname.startsWith("/embed/")) {
        return `https://www.youtube.com${parsed.pathname}${parsed.search}`;
      }

      if (parsed.pathname === "/watch") {
        const id = parsed.searchParams.get("v");
        return id ? `https://www.youtube.com/embed/${id}?rel=0` : null;
      }

      if (parsed.pathname.startsWith("/shorts/")) {
        const id = parsed.pathname.split("/")[2];
        return id ? `https://www.youtube.com/embed/${id}?rel=0` : null;
      }

      if (parsed.pathname.startsWith("/live/")) {
        const id = parsed.pathname.split("/")[2];
        return id ? `https://www.youtube.com/embed/${id}?rel=0` : null;
      }
    }

    return null;
  } catch {
    return null;
  }
};

const getVimeoEmbedSrc = (url = "") => {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./, "");

    if (!host.endsWith("vimeo.com")) return null;

    const parts = parsed.pathname.split("/").filter(Boolean);
    const id = parts[parts.length - 1];

    return /^\d+$/.test(id) ? `https://player.vimeo.com/video/${id}` : null;
  } catch {
    return null;
  }
};

const getDailymotionEmbedSrc = (url = "") => {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./, "");

    if (host === "dai.ly") {
      const id = parsed.pathname.slice(1);
      return id ? `https://www.dailymotion.com/embed/video/${id}` : null;
    }

    if (host.endsWith("dailymotion.com")) {
      const parts = parsed.pathname.split("/").filter(Boolean);
      const videoIndex = parts.indexOf("video");

      if (videoIndex !== -1 && parts[videoIndex + 1]) {
        return `https://www.dailymotion.com/embed/video/${parts[videoIndex + 1]}`;
      }
    }

    return null;
  } catch {
    return null;
  }
};

const getMediaKind = (url = "") => {
  if (!url) return "none";

  if (isImageUrl(url)) return "image";
  if (getYouTubeEmbedSrc(url)) return "youtube";
  if (getVimeoEmbedSrc(url)) return "vimeo";
  if (getDailymotionEmbedSrc(url)) return "dailymotion";
  if (isDirectVideoUrl(url)) return "video";

  if (/(video|player|embed|stream|youtube|vimeo|dailymotion)/i.test(url)) {
    return "iframe";
  }

  return "image";
};

/* =========================
   États UI
========================= */

const PageSkeleton = () => {
  return (
    <main className="min-h-screen bg-slate-50 pt-[calc(var(--site-navbar-h)+48px)] dark:bg-slate-950">
      <div className="mx-auto max-w-6xl px-4 pb-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <div className="mx-auto h-9 w-52 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
          <div className="mx-auto mt-6 h-12 w-2/3 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800" />
          <div className="mx-auto mt-5 h-5 w-3/4 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
        </div>

        <div className="mt-12 space-y-8">
          {Array.from({ length: 3 }).map((_, index) => (
            <div
              key={index}
              className="h-72 animate-pulse rounded-[2.5rem] bg-slate-200 dark:bg-slate-800"
            />
          ))}
        </div>
      </div>
    </main>
  );
};

const EmptyState = ({ title, text }) => {
  return (
    <div className="rounded-[2rem] border border-dashed border-slate-300 bg-white/80 p-10 text-center shadow-sm dark:border-white/10 dark:bg-white/5">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-100 text-primary dark:bg-orange-500/10">
        <FaFileAlt />
      </div>

      <h2 className="font-barlow text-2xl font-bold text-slate-950 dark:text-white">
        {title}
      </h2>

      <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-slate-500 dark:text-slate-400">
        {text}
      </p>
    </div>
  );
};

/* =========================
   Médias
========================= */

const ResponsiveIframe = ({ src, title }) => {
  return (
    <div className="overflow-hidden rounded-[2rem] border border-white/10 bg-slate-950 shadow-2xl">
      <div className="relative w-full pt-[56.25%]">
        <iframe
          className="absolute inset-0 h-full w-full"
          src={src}
          title={title || "Vidéo"}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
      </div>
    </div>
  );
};

const Html5Video = ({ src }) => {
  return (
    <div className="overflow-hidden rounded-[2rem] border border-white/10 bg-slate-950 shadow-2xl">
      <video
        className="w-full"
        src={src}
        controls
        preload="metadata"
        playsInline
      />
    </div>
  );
};

const MediaRenderer = ({ url, alt, onImageClick }) => {
  const resolvedUrl = resolveMediaUrl(url);
  const kind = getMediaKind(resolvedUrl);

  if (!resolvedUrl) return null;

  if (kind === "image") {
    return (
      <div className="group relative overflow-hidden rounded-[2rem] border border-slate-200 bg-slate-50 shadow-xl dark:border-white/10 dark:bg-white/5">
        <button
          type="button"
          onClick={() => onImageClick?.(resolvedUrl, alt)}
          className="block w-full"
          title="Agrandir l’image"
        >
          <img
            src={resolvedUrl}
            alt={alt || "Illustration"}
            loading="lazy"
            decoding="async"
            className="max-h-[560px] w-full object-contain transition duration-500 group-hover:scale-[1.02]"
            onError={(event) => {
              event.currentTarget.src = FALLBACK_IMAGE;
            }}
          />
        </button>

        <button
          type="button"
          onClick={() => onImageClick?.(resolvedUrl, alt)}
          className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full border border-white/40 bg-white/95 text-slate-900 shadow-xl backdrop-blur-xl transition hover:bg-primary hover:text-white"
          aria-label="Agrandir l’image"
        >
          <FaExpandAlt />
        </button>

        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1.5 bg-gradient-to-r from-transparent via-primary to-transparent opacity-90" />
      </div>
    );
  }

  if (kind === "youtube") {
    return <ResponsiveIframe src={getYouTubeEmbedSrc(resolvedUrl)} title={alt} />;
  }

  if (kind === "vimeo") {
    return <ResponsiveIframe src={getVimeoEmbedSrc(resolvedUrl)} title={alt} />;
  }

  if (kind === "dailymotion") {
    return (
      <ResponsiveIframe src={getDailymotionEmbedSrc(resolvedUrl)} title={alt} />
    );
  }

  if (kind === "video") {
    return <Html5Video src={resolvedUrl} />;
  }

  return <ResponsiveIframe src={resolvedUrl} title={alt} />;
};

/* =========================
   Modale image
========================= */

const ImagePreviewModal = ({ image, onClose }) => {
  useEffect(() => {
    if (!image) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => window.removeEventListener("keydown", onKeyDown);
  }, [image, onClose]);

  if (!image) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/85 px-3 py-5 backdrop-blur-sm sm:px-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="relative flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-[2rem] border border-white/10 bg-slate-950 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-4 border-b border-white/10 bg-slate-950/95 p-4">
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/45">
              Aperçu image
            </p>

            <h3 className="mt-1 line-clamp-1 font-barlow text-lg font-bold text-white">
              {image.alt || "Image"}
            </h3>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-slate-950 shadow-lg transition hover:scale-105"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        <div className="flex flex-1 items-center justify-center overflow-auto bg-slate-900 p-4">
          <img
            src={image.src}
            alt={image.alt || "Image agrandie"}
            className="max-h-[78vh] w-full object-contain"
            onError={(event) => {
              event.currentTarget.src = FALLBACK_IMAGE;
            }}
          />
        </div>
      </div>
    </div>
  );
};

/* =========================
   Article
========================= */

const ArticleBlock = ({ article, index, onImageClick }) => {
  const mediaUrl = article?.url_image || "";
  const hasMedia = Boolean(mediaUrl);
  const hasText = Boolean(article?.contenu_texte);

  const safeHtml = useMemo(
    () => sanitizeHtml(article?.contenu_texte || ""),
    [article?.contenu_texte]
  );

  const isReversedDesktop = index % 2 !== 0 && hasMedia && hasText;

  if (!hasMedia && !hasText) return null;

  if (hasMedia && !hasText) {
    return (
      <article className="overflow-hidden rounded-[2.5rem] border border-slate-200 bg-white/90 p-4 shadow-xl backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/90 sm:p-5">
        {article?.titre_article && (
          <div className="mb-5 flex items-center gap-3 px-2">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary dark:bg-primary/20">
              <FaImage />
            </div>

            <h2 className="font-barlow text-2xl font-bold text-slate-950 dark:text-white">
              {article.titre_article}
            </h2>
          </div>
        )}

        <MediaRenderer
          url={mediaUrl}
          alt={article?.titre_article}
          onImageClick={onImageClick}
        />
      </article>
    );
  }

  if (!hasMedia && hasText) {
    return (
      <article className="overflow-hidden rounded-[2.5rem] border border-slate-200 bg-white/90 p-6 shadow-xl backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/90 sm:p-8 lg:p-10">
        {article?.titre_article && (
          <div className="mb-6 flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary dark:bg-primary/20">
              <FaFileAlt />
            </div>

            <h2 className="font-barlow text-3xl font-bold leading-tight text-slate-950 dark:text-white">
              {article.titre_article}
            </h2>
          </div>
        )}

        <div
          className={cn(
            "prose prose-slate max-w-none dark:prose-invert",
            "text-sm leading-7 text-slate-600 dark:text-slate-300 sm:text-base sm:leading-8",
            "prose-a:font-bold prose-a:text-primary",
            "prose-headings:font-barlow prose-headings:font-bold",
            "dark:[&_strong]:!text-white dark:[&_b]:!text-white"
          )}
          dangerouslySetInnerHTML={{ __html: safeHtml }}
        />

        <div
          aria-hidden
          className="mt-8 h-1.5 w-full rounded-full bg-gradient-to-r from-transparent via-primary to-transparent opacity-90"
        />
      </article>
    );
  }

  return (
    <article className="overflow-hidden rounded-[2.5rem] border border-slate-200 bg-white/90 p-5 shadow-xl backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/90 sm:p-6 lg:p-7">
      <div className="grid items-center gap-7 lg:grid-cols-2">
        {/* Mobile : image toujours en haut.
            Desktop : alternance gauche/droite selon l’index. */}
        <div
          className={cn(
            "w-full",
            isReversedDesktop ? "lg:order-2" : "lg:order-1"
          )}
        >
          <MediaRenderer
            url={mediaUrl}
            alt={article?.titre_article}
            onImageClick={onImageClick}
          />
        </div>

        <div
          className={cn(
            "w-full rounded-[2rem] bg-slate-50 p-5 dark:bg-white/5 sm:p-7",
            isReversedDesktop ? "lg:order-1" : "lg:order-2"
          )}
        >
          {article?.titre_article && (
            <div className="mb-5">
              <p className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-orange-700 dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
                <FaPhotoVideo />
                Article
              </p>

              <h2 className="mt-4 font-barlow text-2xl font-bold leading-tight text-slate-950 dark:text-white sm:text-3xl">
                {article.titre_article}
              </h2>
            </div>
          )}

          <div
            className={cn(
              "prose prose-slate max-w-none dark:prose-invert",
              "text-sm leading-7 text-slate-600 dark:text-slate-300 sm:text-base sm:leading-8",
              "prose-a:font-bold prose-a:text-primary",
              "prose-headings:font-barlow prose-headings:font-bold",
              "dark:[&_strong]:!text-white dark:[&_b]:!text-white"
            )}
            dangerouslySetInnerHTML={{ __html: safeHtml }}
          />
        </div>
      </div>

      <div
        aria-hidden
        className="mt-7 h-1.5 w-full rounded-full bg-gradient-to-r from-transparent via-primary to-transparent opacity-90"
      />
    </article>
  );
};

/* =========================
   Page principale
========================= */

const PagePersonnalisee = () => {
  useScrollToTop();

  const navigate = useNavigate();
  const { slug } = useParams();

  const [page, setPage] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [previewImage, setPreviewImage] = useState(null);

  useEffect(() => {
    let mounted = true;

    const fetchPage = async () => {
      try {
        setLoading(true);
        setErrorMsg("");

        const pageData = await getPageBySlug(slug);

        if (!mounted) return;

        if (!pageData?.id) {
          setPage(null);
          setArticles([]);
          setErrorMsg("Cette page personnalisée est introuvable.");
          return;
        }

        setPage(pageData);

        const articlesData = await getAllArticlesByPage(pageData.id);

        if (!mounted) return;

        const sortedArticles = Array.isArray(articlesData)
          ? [...articlesData].sort((a, b) => {
              const orderA = Number.isFinite(Number(a?.ordre))
                ? Number(a.ordre)
                : Number.MAX_SAFE_INTEGER;

              const orderB = Number.isFinite(Number(b?.ordre))
                ? Number(b.ordre)
                : Number.MAX_SAFE_INTEGER;

              if (orderA !== orderB) return orderA - orderB;

              return Number(a?.id || 0) - Number(b?.id || 0);
            })
          : [];

        setArticles(sortedArticles);
      } catch (error) {
        console.error("Erreur chargement page personnalisée :", error);

        if (mounted) {
          setErrorMsg("Impossible de charger cette page personnalisée.");
          setPage(null);
          setArticles([]);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchPage();

    return () => {
      mounted = false;
    };
  }, [slug]);

  const safeIntroduction = useMemo(
    () => sanitizeHtml(page?.introduction || ""),
    [page?.introduction]
  );

  const stats = useMemo(() => {
    return {
      articles: articles.length,
      medias: articles.filter((article) => article?.url_image).length,
      videos: articles.filter((article) => {
        const resolved = resolveMediaUrl(article?.url_image || "");
        const kind = getMediaKind(resolved);

        return ["youtube", "vimeo", "dailymotion", "video", "iframe"].includes(
          kind
        );
      }).length,
    };
  }, [articles]);

  const handleImageClick = (src, alt) => {
    setPreviewImage({
      src,
      alt,
    });
  };

  if (loading) return <PageSkeleton />;

  return (
    <main className="relative min-h-screen overflow-hidden bg-slate-50 pt-[calc(var(--site-navbar-h)+48px)] text-slate-950 dark:bg-slate-950 dark:text-white">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.13),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_32%)]" />

      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.14)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.14)_1px,transparent_1px)] bg-[size:42px_42px] opacity-40 dark:opacity-20" />

      <div className="relative mx-auto max-w-6xl px-4 pb-16 sm:px-6 lg:px-8">
        <div className="mb-8 flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 px-4 py-2.5 text-sm font-bold text-slate-700 shadow-sm backdrop-blur transition hover:-translate-y-0.5 hover:border-primary/30 hover:text-primary dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
          >
            <FaArrowLeft className="text-xs" />
            Retour
          </button>
        </div>

        {errorMsg ? (
          <EmptyState title="Page indisponible" text={errorMsg} />
        ) : (
          <>
            <section className="mx-auto max-w-3xl text-center">
              <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-orange-700 shadow-sm dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
                <FaLayerGroup />
                Page du club
              </div>

              <h1 className="mt-6 font-barlow text-4xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-5xl lg:text-6xl">
                {page?.titre || "Page personnalisée"}
              </h1>

              {safeIntroduction && (
                <div
                  className={cn(
                    "mx-auto mt-6 max-w-3xl",
                    "prose prose-slate max-w-none dark:prose-invert",
                    "text-base leading-8 text-slate-600 dark:text-slate-300",
                    "prose-a:font-bold prose-a:text-primary",
                    "prose-headings:font-barlow prose-headings:font-bold",
                    "dark:[&_strong]:!text-white dark:[&_b]:!text-white"
                  )}
                  dangerouslySetInnerHTML={{ __html: safeIntroduction }}
                />
              )}
            </section>

            {articles.length > 0 && (
              <section className="mx-auto mt-10 grid max-w-4xl gap-4 sm:grid-cols-3">
                <div className="rounded-[1.5rem] border border-slate-200 bg-white/80 p-4 text-center shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-white/5">
                  <FaFileAlt className="mx-auto mb-3 text-2xl text-primary" />
                  <p className="font-barlow text-3xl font-bold text-slate-950 dark:text-white">
                    {stats.articles}
                  </p>
                  <p className="mt-1 text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
                    Article{stats.articles > 1 ? "s" : ""}
                  </p>
                </div>

                <div className="rounded-[1.5rem] border border-slate-200 bg-white/80 p-4 text-center shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-white/5">
                  <FaImage className="mx-auto mb-3 text-2xl text-primary" />
                  <p className="font-barlow text-3xl font-bold text-slate-950 dark:text-white">
                    {stats.medias}
                  </p>
                  <p className="mt-1 text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
                    Média{stats.medias > 1 ? "s" : ""}
                  </p>
                </div>

                <div className="rounded-[1.5rem] border border-slate-200 bg-white/80 p-4 text-center shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-white/5">
                  <FaPlayCircle className="mx-auto mb-3 text-2xl text-primary" />
                  <p className="font-barlow text-3xl font-bold text-slate-950 dark:text-white">
                    {stats.videos}
                  </p>
                  <p className="mt-1 text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
                    Vidéo{stats.videos > 1 ? "s" : ""}
                  </p>
                </div>
              </section>
            )}

            <section className="mt-12">
              {articles.length === 0 ? (
                <EmptyState
                  title="Aucun contenu publié"
                  text="Les articles de cette page apparaîtront ici dès qu’ils seront ajoutés dans l’administration."
                />
              ) : (
                <div className="space-y-8">
                  {articles.map((article, index) => (
                    <ArticleBlock
                      key={article.id || `${article.titre_article}-${index}`}
                      article={article}
                      index={index}
                      onImageClick={handleImageClick}
                    />
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </div>

      <ImagePreviewModal
        image={previewImage}
        onClose={() => setPreviewImage(null)}
      />
    </main>
  );
};

export default PagePersonnalisee;